// src/pages/categorias/EditarCategoria.js
import React, { useState, useEffect } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import { FormLabel, Offcanvas, Alert } from 'react-bootstrap'
import { PanelFooter, PanelHeader } from 'components/Panel'
import FileUpload from 'components/FileUpload/FileUpload'
import FilePreview from 'components/FileUpload/FilePreview'
import AtributosSelector from 'components/AtributosSelector'
import { useForm } from 'hooks/useForm'
import { useFileUpload } from 'hooks/useFileUpload'
import { API_UPLOADS_URL, API_URL } from 'config/general'
import { CATEGORIAS_NO_IMAGE } from 'config/categorias'
import { toast } from 'react-toastify'
import api from 'api/api'

function EditarCategoria(props) {
	const { categoriaOriginal, categoriasDisponibles, mostrar, guardar, cancelar } = props

	const [erroresValidacion, setErroresValidacion] = useState({})
	const [loading, setLoading] = useState(false)
	const [fileUploadKey, setFileUploadKey] = useState(0)
	const [archivoSeleccionado, setArchivoSeleccionado] = useState(null)

	// Estados para atributos
	const [atributosTiposDisponibles, setAtributosTiposDisponibles] = useState([])
	const [atributosTipoIdsSeleccionados, setAtributosTipoIdsSeleccionados] = useState([])
	const [atributosTipoIdsOriginales, setAtributosTipoIdsOriginales] = useState([])
	const [loadingAtributos, setLoadingAtributos] = useState(false)
	const [itemsAfectados, setItemsAfectados] = useState(0)

	// Usar el hook para manejar la imagen
	const imageUpload = useFileUpload({
		maxFiles: 1,
		maxFileSize: 2000000, // 2MB
		allowedTypes: ['image/jpeg', 'image/png', 'image/webp', '.jpg', '.jpeg', '.png', '.webp'],
		endpoint: 'a/multimedia',
		fieldName: 'foto',
		apiUrlPrefix: API_UPLOADS_URL,
		uploadOnSelect: false,
		maxFilesMessage: {
			type: 'info',
			message: 'Se reemplazará la imagen existente por la nueva seleccionada'
		}
	})

	// Cargar tipos de atributos disponibles
	const cargarAtributosTipos = async () => {
		try {
			setLoadingAtributos(true)
			const { datos } = await api.get('a/config-atributos')
			setAtributosTiposDisponibles(datos.tipos || [])
		} catch (error) {
			console.error('Error cargando tipos de atributos:', error)
			// No mostrar error ya que es opcional
		} finally {
			setLoadingAtributos(false)
		}
	}

	// Cargar atributos asignados a la categoría
	const cargarAtributosCategoria = async (categoriaId) => {
		try {
			const { datos } = await api.get(`a/jerarquias/${categoriaId}/atributos`)
			const ids = datos.atributosTipoIds || []
			setAtributosTipoIdsSeleccionados(ids)
			setAtributosTipoIdsOriginales(ids)
			setItemsAfectados(datos.itemsAfectados || 0)
		} catch (error) {
			console.error('Error cargando atributos de categoría:', error)
			// No es crítico, la categoría puede no tener atributos
		}
	}

	// Filtrar categorías disponibles para evitar ciclos (no permitir seleccionar como padre a sí mismo o a sus hijos)
	const obtenerCategoriasDisponibles = () => {
		if (!categoriaOriginal) return []

		// Función recursiva para obtener todos los IDs de subcategorías
		const obtenerSubcategoriasIds = (categoria) => {
			let ids = [categoria.id]
			if (categoria.subcategorias && categoria.subcategorias.length > 0) {
				categoria.subcategorias.forEach(sub => {
					ids = [...ids, ...obtenerSubcategoriasIds(sub)]
				})
			}
			return ids
		}

		// Obtener todos los IDs que deben excluirse
		const idsExcluidos = categoriaOriginal ? obtenerSubcategoriasIds(categoriaOriginal) : []

		// Filtrar categorías disponibles
		return [
			{ value: '', label: 'Categoría principal' },
			...categoriasDisponibles
				.filter(cat => !idsExcluidos.includes(cat.id))
				.map(cat => ({
					value: cat.id,
					label: cat.nombre
				}))
		]
	}

	const opcionesCategorias = obtenerCategoriasDisponibles()

	// Preparar datos iniciales basados en la categoría a editar
	const prepararDatosIniciales = () => {
		if (!categoriaOriginal) return {
			nombre: '',
			jerarquiaPapaId: '',
			archivo: null
		}

		return {
			nombre: categoriaOriginal.nombre || '',
			jerarquiaPapaId: categoriaOriginal.jerarquiaPapa && categoriaOriginal.jerarquiaPapa.id
				? categoriaOriginal.jerarquiaPapa.id
				: '',
			archivo: null,
			eliminaImagen: false
		}
	}

	useEffect(() => {
		if (categoriaOriginal && mostrar) {
			updateInitialData(prepararDatosIniciales())

			// Limpiar archivo seleccionado previamente
			setArchivoSeleccionado(null)

			// Cargar la imagen existente al hook si existe
			if (categoriaOriginal.multimedia) {
				const imagen = {
					id: categoriaOriginal.multimedia.id,
					url: API_URL + categoriaOriginal.multimedia.url,
					nombre: categoriaOriginal.multimedia.nombre || `imagen_${categoriaOriginal.id}`,
					type: 'image/jpeg' // Determinar tipo por extensión si es necesario
				}
				imageUpload.initializeWithExistingFiles([imagen])
			} else {
				imageUpload.reset()
			}

			// Cargar tipos de atributos y los asignados a esta categoría
			cargarAtributosTipos()
			cargarAtributosCategoria(categoriaOriginal.id)
		}
	}, [categoriaOriginal, mostrar])

	const handleClose = () => {
		imageUpload.reset()
		setArchivoSeleccionado(null)
		setAtributosTipoIdsSeleccionados([])
		setAtributosTipoIdsOriginales([])
		cancelar()
	}

	// Verificar si hubo cambios en los atributos
	const atributosCambiaron = () => {
		if (atributosTipoIdsSeleccionados.length !== atributosTipoIdsOriginales.length) {
			return true
		}
		const sortedSelected = [...atributosTipoIdsSeleccionados].sort()
		const sortedOriginal = [...atributosTipoIdsOriginales].sort()
		return !sortedSelected.every((val, idx) => val === sortedOriginal[idx])
	}

	const handleProcesar = async (datos) => {
		if (validarDatos()) {
			setLoading(true)

			try {
				// Crear un objeto con solo los campos que han cambiado
				const cambios = {}

				// Verificar cada campo para ver si ha cambiado
				if (datos.nombre !== categoriaOriginal.nombre) {
					cambios.nombre = datos.nombre
				}

				const categoriaPadreIdOriginal = categoriaOriginal.jerarquiaPapa?.id || ''
				if (datos.jerarquiaPapaId !== categoriaPadreIdOriginal) {
					cambios.jerarquiaPapaId = datos.jerarquiaPapaId || null
				}

				// Subir el archivo directamente si hay uno seleccionado en el estado local
				if (archivoSeleccionado) {
					// Crear FormData manualmente
					const formData = new FormData()
					formData.append('foto', archivoSeleccionado)

					try {
						// Llamada directa a la API
						const respuesta = await api.post('a/multimedia', formData)

						if (respuesta && respuesta.datos && respuesta.datos.id) {
							cambios.multimediaId = respuesta.datos.id
						}
					} catch (error) {
						console.error("Error al subir imagen:", error)
						toast.error("Error al subir la imagen")
					}
				}

				// Verificar eliminación de imagen existente
				if (
					imageUpload.filesToDelete.length > 0 &&
					categoriaOriginal.multimedia &&
					imageUpload.filesToDelete.includes(categoriaOriginal.multimedia.id)
				) {
					cambios.eliminarMultimedia = true
				}

				// Guardar cambios de la categoría si hay
				if (Object.keys(cambios).length > 0) {
					await guardar(cambios)
				}

				// Guardar cambios de atributos si cambiaron
				if (atributosCambiaron()) {
					try {
						await api.put(`a/jerarquias/${categoriaOriginal.id}/atributos`, {
							atributosTipoIds: atributosTipoIdsSeleccionados
						})
						// Mostrar mensaje solo si los atributos fueron lo único que cambió
						if (Object.keys(cambios).length === 0) {
							toast.success("Atributos de la categoría actualizados")
						}
					} catch (error) {
						console.error("Error al guardar atributos:", error)
						toast.error("Error al guardar los atributos")
					}
				}

				// Si hubo cambios en la categoría o atributos
				if (Object.keys(cambios).length > 0 || atributosCambiaron()) {
					handleClose()
				} else {
					// No hubo cambios
					handleClose()
				}
			} catch (error) {
				console.error("Error general:", error)
				toast.error("Error al guardar los cambios")
			} finally {
				setLoading(false)
			}
		}
	}

	const validarDatos = () => {
		const camposRequeridos = ['nombre']
		const nuevosErrores = {}

		camposRequeridos.forEach(campo => {
			if (!data[campo] || data[campo].toString().trim() === '') {
				nuevosErrores[campo] = 'Este campo es requerido'
			} else {
				nuevosErrores[campo] = null
			}
		})

		setErroresValidacion(nuevosErrores)
		return Object.keys(nuevosErrores).filter(key => nuevosErrores[key] !== null).length === 0
	}

	const handleSeleccionImagen = (files) => {
		if (files && files.length > 0) {
			// Guardar referencia al archivo en estado local
			setArchivoSeleccionado(files[0])

			// Marcar la imagen actual para eliminación si existe
			if (imageUpload.uploadedFiles.length > 0) {
				imageUpload.markFileForDeletion(imageUpload.uploadedFiles[0])
			}

			// Añadir la nueva imagen al hook (esto puede que no persista después del remontaje)
			imageUpload.handleSelectFiles(files)
		}
	}

	const handleUndoImageDeletion = (file) => {
		// Limpiar referencia al archivo en estado local
		setArchivoSeleccionado(null)

		// Liberar URLs de previsualización
		imageUpload.selectedFiles.forEach((selectedFile) => {
			if (selectedFile.previewUrl) {
				URL.revokeObjectURL(selectedFile.previewUrl)
			}
		})

		// Reset del estado del hook
		imageUpload.reset()

		// Incrementar el key para forzar el remontaje del componente
		setFileUploadKey(prevKey => prevKey + 1)

		// Re-inicializar con la imagen original
		if (categoriaOriginal && categoriaOriginal.multimedia) {
			const imagen = {
				id: categoriaOriginal.multimedia.id,
				url: API_URL + categoriaOriginal.multimedia.url,
				nombre: categoriaOriginal.multimedia.nombre || `imagen_${categoriaOriginal.id}`,
				type: categoriaOriginal.multimedia.type || 'image/jpeg',
			}

			// Usar setTimeout para asegurar que el reset se complete antes
			setTimeout(() => {
				imageUpload.initializeWithExistingFiles([imagen])
			}, 0)
		}

		toast.info('Imagen original restaurada')
	}

	const { data, handleChange, handleSubmit, updateInitialData } = useForm(
		prepararDatosIniciales(),
		() => true, // Validación básica (la hacemos manual con validarDatos)
		handleProcesar
	)

	// render
	return (
		<Offcanvas show={mostrar} onHide={handleClose} placement="end">
			<PanelHeader onClick={handleClose} iconClass="fa-arrow-left" btnText="Atrás" text="Editar categoría" />
			<Offcanvas.Body id="editar-categoria">
				<h5 className="mb-3">Campos de la categoría</h5>
				{categoriaOriginal && (
					<Row>
						<Col sm={12} md={6}>
							<Form onSubmit={handleSubmit} className="mb-4">
								<Form.Group className="mb-3" controlId="nombre">
									<FormLabel>Nombre*</FormLabel>
									<Form.Control
										value={data.nombre}
										onChange={(e) => handleChange(e.target.value, 'nombre')}
										isInvalid={erroresValidacion.nombre}
									/>
									<Form.Control.Feedback type="invalid">{erroresValidacion.nombre}</Form.Control.Feedback>
								</Form.Group>

								<Form.Group className="mb-3" controlId="jerarquiaPapaId">
									<FormLabel>Categoría padre</FormLabel>
									<Form.Select
										value={data.jerarquiaPapaId}
										onChange={(e) => handleChange(e.target.value, 'jerarquiaPapaId')}
									>
										{opcionesCategorias.map((opcion) => (
											<option key={opcion.value} value={opcion.value}>
												{opcion.label}
											</option>
										))}
									</Form.Select>
									<Form.Text className="text-muted">
										Si no seleccionas una categoría padre, se establecerá como categoría principal.
									</Form.Text>
								</Form.Group>
							</Form>

							{/* Atributos por defecto */}
							<hr className="my-4" />

							<h5 className="mb-3">
								<i className="fa fa-sliders me-2"></i>
								Atributos por defecto
							</h5>

							<AtributosSelector
								atributosTiposDisponibles={atributosTiposDisponibles}
								atributosTipoIdsSeleccionados={atributosTipoIdsSeleccionados}
								onChange={setAtributosTipoIdsSeleccionados}
								disabled={loading}
								loading={loadingAtributos}
								itemsAfectados={itemsAfectados}
							/>

							{atributosCambiaron() && (
								<Alert variant="info" className="mt-3">
									<i className="fa fa-info-circle me-2"></i>
									<small>Los cambios en atributos se aplicarán a los productos de esta categoría.</small>
								</Alert>
							)}
						</Col>
						<Col sm={12} md={6}>
							<h5 className="mb-3">Imagen de la categoría</h5>

							{/* Mostrar imagen existente con FilePreview */}
							{imageUpload.uploadedFiles.length > 0 && (
								<div className="mb-4">
									<FilePreview
										files={imageUpload.uploadedFiles}
										onRemove={imageUpload.markFileForDeletion}
										onUndo={handleUndoImageDeletion} // Usar nuestro manejador personalizado
										title="Imagen actual"
										showFileSize={false}
										markedForDeletion={imageUpload.filesToDelete}
										emptyMessage="No hay imagen guardada."
										useImageCover={false}
									/>
								</div>
							)}

							{/* Mostrar componente para seleccionar nueva imagen */}
							<FileUpload
								key={fileUploadKey} // Esta es la adición clave
								title="Nueva imagen"
								accept=".png, .jpg, .jpeg, .webp"
								buttonText="Elegir"
								buttonTextSingular="imagen"
								updateFiles={handleSeleccionImagen}
								maxFileSizeInBytes={2000000}
								showPreview={true}
								previewTitle="Nueva imagen seleccionada"
							/>

							<Alert variant="info" className="mt-3">
								<i className="fa fa-info-circle me-2"></i>
								Formato recomendado: Imagen cuadrada de al menos 300x300 píxeles en formato JPG, PNG o WebP.
							</Alert>

							{!imageUpload.uploadedFiles.length && !imageUpload.selectedFiles.length && !archivoSeleccionado && (
								<div className="text-center mt-4">
									<p className="text-muted">Sin imagen actualmente</p>
									<img
										src={CATEGORIAS_NO_IMAGE}
										alt="Placeholder"
										className="img-fluid mt-2"
										style={{ maxHeight: "200px", opacity: "0.7" }}
									/>
								</div>
							)}
						</Col>
					</Row>
				)}
			</Offcanvas.Body>
			<PanelFooter>
				<Button variant="outline-secondary" onClick={handleClose} className="me-2" disabled={loading}>
					Cancelar
				</Button>
				<Button
					variant="primary"
					onClick={() => {
						if (validarDatos()) {
							handleSubmit()
						}
					}}
					disabled={loading || imageUpload.isLoading}
				>
					{(loading || imageUpload.isLoading) ? (
						<><i className="fa fa-spinner fa-spin me-2"></i> Guardando...</>
					) : 'Guardar'}
				</Button>
			</PanelFooter>
		</Offcanvas>
	)
}

export default EditarCategoria